import { App } from 'antd';
import { useAuth } from 'react-oidc-context';
import { hasFrontDeskRole } from '../utils/user.ts';
import { useCurrentContextStore } from './store/useCurrentContextStore.ts';
import { usePrintDoctorLabel } from './usePrintDoctorLabel.ts';
import { type EmptyLabelPatient, emptyLabelPayload, usePrintEmptyLabel } from './usePrintEmptyLabel.ts';

type ExtraLabelsDoctor = { addEmptyLabels: number; zplLabelTemplate: string; addZplLabels: number } | null | undefined;

type PrintedRequest = { id: string; orders: Array<{ id: string }> };

const NO_PATIENT: EmptyLabelPatient = {
  title: '',
  lastName: '',
  firstName: '',
  svnr: '',
  birthday: '1970-01-01',
  insuranceCode: '',
};

const ignore = () => {};

/** The Zuweiser's Leer- and Zuweiseretiketten that follow a print of an Anforderung's labels, as on the desktop client. */
export const usePrintExtraLabels = () => {
  const auth = useAuth();
  const { message } = App.useApp();
  const { currentDoctorId, primaryDoctorId } = useCurrentContextStore();
  const printEmptyLabel = usePrintEmptyLabel();
  const printDoctorLabel = usePrintDoctorLabel();
  const doctorIdForTemplate = hasFrontDeskRole(auth.user) ? primaryDoctorId : currentDoctorId;

  const printEmptyLabels = (count: number, patient: EmptyLabelPatient, requestId: string) =>
    printEmptyLabel(
      emptyLabelPayload(currentDoctorId, patient),
      count,
      () => message.error('Beim Drucken der Leeretiketten ist ein Fehler aufgetreten'),
      ignore,
      requestId
    );

  const printDoctorLabels = (count: number, requestId: string, orderIds: string[]) => {
    for (const orderId of orderIds) {
      printDoctorLabel(
        { doctorIdForTemplate, requestId, orderId },
        count,
        () => message.error('Beim Drucken der Zuweiseretiketten ist ein Fehler aufgetreten'),
        ignore
      );
    }
  };

  return (doctor: ExtraLabelsDoctor, patient: EmptyLabelPatient | null | undefined, request: PrintedRequest) => {
    if (doctor?.addEmptyLabels) {
      printEmptyLabels(doctor.addEmptyLabels, patient ?? NO_PATIENT, request.id);
    }
    if (doctor?.zplLabelTemplate && doctor.addZplLabels) {
      printDoctorLabels(
        doctor.addZplLabels,
        request.id,
        request.orders.map(it => it.id)
      );
    }
  };
};
